import React, { useMemo } from 'react';
import { Table, Button, Space, Card, Input, Select, DatePicker, Tag, Row, Col, Statistic, Tooltip } from 'antd';
import {
  PlusOutlined, EditOutlined, SearchOutlined, DeleteOutlined,
  ClockCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined,
  SyncOutlined, SettingOutlined, ThunderboltOutlined
} from '@ant-design/icons';
import { formatTimeStr, formatDateStr, getStatusTagColor, ATTENDANCE_STATUSES, normalizeStatusCode } from '../attendanceUtils';

const { Option } = Select;

export default function DailyAttendanceTab({
  attendanceData = [],
  attLoading = false,
  selectedDate,
  setSelectedDate,
  personTypeFilter,
  setPersonTypeFilter,
  ayFilter,
  setAyFilter,
  clusterFilter,
  setClusterFilter,
  standardFilter,
  setStandardFilter,
  combinationFilter = 'all',
  setCombinationFilter,
  availableCombinations = [],
  attSearch,
  setAttSearch,
  academicYears = [],
  availableClusters = [],
  availableStandards = [],
  isStudio = false,
  onRecalculate,
  recalculating = false,
  onOpenAttModal,
  onOpenShiftSettingsModal
}) {
  // Statistics for all 6 statuses
  const attStats = useMemo(() => {
    const total = attendanceData.length;
    let present = 0;
    let absent = 0;
    let halfDay = 0;
    let overtime = 0;
    let late = 0;
    let leaveEarly = 0;

    attendanceData.forEach(item => {
      const st = String(item.status || '').toUpperCase().trim();
      if (st === 'PRESENT' || st === 'P') present++;
      else if (st === 'ABSENT' || st === 'A') absent++;
      else if (st === 'HALF_DAY' || st === 'HALF DAY' || st === 'HD') halfDay++;
      else if (st === 'OVERTIME' || st === 'OT') overtime++;
      else if (st === 'LATE' || st === 'L') late++;
      else if (st === 'LEAVE_EARLY' || st === 'LEAVE EARLY' || st === 'LE' || st === 'ON_LEAVE' || st === 'ON LEAVE' || st === 'LEAVE') leaveEarly++;
      else present++;
    });

    return { total, present, absent, halfDay, overtime, late, leaveEarly };
  }, [attendanceData]);

  // Filtered List
  const filteredAttendance = useMemo(() => {
    return attendanceData.filter(item => {
      if (combinationFilter && combinationFilter !== 'all' && item.person_type !== 'STAFF') {
        if (item.combination !== combinationFilter) return false;
      }
      if (!attSearch) return true;
      const q = attSearch.toLowerCase();
      const name = String(item.staff_name || item.candidate_name || item.name || '').toLowerCase();
      const code = String(item.staff_code || item.candidate_id || '').toLowerCase();
      const bioId = String(item.biometric_user_id || '').toLowerCase();
      const comb = String(item.combination || '').toLowerCase();
      const cluster = String(item.cluster_name || '').toLowerCase();
      const std = String(item.standard_name || '').toLowerCase();
      return name.includes(q) || code.includes(q) || bioId.includes(q) || comb.includes(q) || cluster.includes(q) || std.includes(q);
    });
  }, [attendanceData, attSearch, combinationFilter]);

  const columns = [
    {
      title: 'Individual Name',
      key: 'name',
      render: (_, record) => {
        const isStaff = record.person_type === 'STAFF';
        const name = isStaff ? record.staff_name : record.candidate_name;
        const sub = isStaff ? `Staff Code: ${record.staff_code || record.staff_id || '-'}` : `Candidate ID: ${record.candidate_id || '-'}`;
        const extraDetails = !isStaff ? (
          (record.cluster_name || record.standard_name || record.combination) ? (
            <div style={{ fontSize: 11, color: 'var(--text-muted, #8c8c8c)', marginTop: 2 }}>
              {[record.cluster_name, record.standard_name, record.combination ? `Comb: ${record.combination}` : null].filter(Boolean).join(' • ')}
            </div>
          ) : null
        ) : (
          record.cluster_name ? (
            <div style={{ fontSize: 11, color: 'var(--text-muted, #8c8c8c)', marginTop: 2 }}>
              Cluster: {record.cluster_name}
            </div>
          ) : null
        );
        return (
          <div>
            <strong style={{ fontSize: 14 }}>{name || 'Unknown User'}</strong>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sub}</div>
            {extraDetails}
          </div>
        );
      }
    },
    {
      title: 'Type',
      dataIndex: 'person_type',
      key: 'person_type',
      render: (type) => (
        <Tag color={type === 'STAFF' ? 'blue' : 'magenta'}>
          {type}
        </Tag>
      )
    },
    {
      title: 'Date',
      dataIndex: 'attendance_date',
      key: 'attendance_date',
      render: (d) => formatDateStr(d)
    },
    {
      title: 'Check-In',
      dataIndex: 'check_in_time',
      key: 'check_in_time',
      render: (t) => t ? <Tag color="cyan">{formatTimeStr(t)}</Tag> : '-'
    },
    {
      title: 'Check-Out',
      dataIndex: 'check_out_time',
      key: 'check_out_time',
      render: (t) => t ? <Tag color="purple">{formatTimeStr(t)}</Tag> : '-'
    },
    {
      title: 'Total Hours',
      dataIndex: 'total_hours',
      key: 'total_hours',
      render: (hrs) => hrs ? `${parseFloat(hrs).toFixed(1)} hrs` : '-'
    },
    {
      title: 'Status (P / A / HD / OT / L / LE)',
      dataIndex: 'status',
      key: 'status',
      filters: ATTENDANCE_STATUSES.map(s => ({ text: s.label, value: s.value })),
      onFilter: (value, record) => {
        const norm = normalizeStatusCode(record.status);
        return norm.status === value || norm.code === value;
      },
      render: (st) => {
        const norm = normalizeStatusCode(st);
        return (
          <Tag color={getStatusTagColor(st)} style={{ fontWeight: 600 }}>
            {norm.label}
          </Tag>
        );
      }
    },
    {
      title: 'Biometric Machine PIN',
      dataIndex: 'biometric_user_id',
      key: 'biometric_user_id',
      render: (id) => id ? <Tag color="geekblue">PIN: {id}</Tag> : <span style={{ color: '#bfbfbf' }}>Manual Entry</span>
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Tooltip title="Edit Record">
          <Button
            type="text"
            icon={<EditOutlined style={{ color: 'var(--primary-color)' }} />}
            onClick={() => onOpenAttModal(record)}
          />
        </Tooltip>
      )
    }
  ];

  const isStaffSelected = personTypeFilter === 'STAFF';

  return (
    <div>
      {/* 7 Stats Summary Cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6} md={3}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Total Entries" value={attStats.total} prefix={<ClockCircleOutlined />} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Present (P)" value={attStats.present} valueStyle={{ color: '#10b981', fontSize: 20 }} prefix={<CheckCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Absent (A)" value={attStats.absent} valueStyle={{ color: '#ef4444', fontSize: 20 }} prefix={<DeleteOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={3}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Half Day (HD)" value={attStats.halfDay} valueStyle={{ color: '#faad14', fontSize: 20 }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Overtime (OT)" value={attStats.overtime} valueStyle={{ color: '#fa541c', fontSize: 20 }} prefix={<ThunderboltOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Late (L)" value={attStats.late} valueStyle={{ color: '#d46b08', fontSize: 20 }} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6} md={4}>
          <Card bodyStyle={{ padding: 12 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Leave Early (LE)" value={attStats.leaveEarly} valueStyle={{ color: '#13c2c2', fontSize: 20 }} prefix={<ExclamationCircleOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Filter & Action Toolbar */}
      <Card bodyStyle={{ padding: 16 }} style={{ marginBottom: 16, background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
        <Row gutter={[10, 10]} align="middle">
          {/* 1. Academic Year Filter */}
          <Col xs={12} sm={6} md={isStaffSelected ? 4 : 3}>
            <Select
              value={ayFilter}
              onChange={(val) => {
                setAyFilter(val);
                if (setClusterFilter) setClusterFilter('all');
                if (setStandardFilter) setStandardFilter('all');
                if (setCombinationFilter) setCombinationFilter('all');
              }}
              style={{ width: '100%' }}
              placeholder="Academic Year"
            >
              <Option value="all">All Academic Years</Option>
              {academicYears.map(ay => (
                <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                  {ay.academic_year}
                </Option>
              ))}
            </Select>
          </Col>

          {/* 2. Person Type Filter */}
          <Col xs={12} sm={6} md={isStaffSelected ? 4 : 3}>
            <Select
              value={isStudio ? 'CANDIDATE' : personTypeFilter}
              onChange={(val) => {
                setPersonTypeFilter(val);
                if (val === 'STAFF') {
                  if (setStandardFilter) setStandardFilter('all');
                  if (setCombinationFilter) setCombinationFilter('all');
                }
              }}
              style={{ width: '100%' }}
              disabled={isStudio}
            >
              {!isStudio && <Option value="all">All Types</Option>}
              {!isStudio && <Option value="STAFF">Staff Only</Option>}
              <Option value="CANDIDATE">Candidate Only</Option>
            </Select>
          </Col>

          {/* 3. Cluster Filter (Visible for Candidates / All) */}
          {!isStaffSelected && (
            <Col xs={12} sm={6} md={3}>
              <Select
                value={clusterFilter}
                onChange={(val) => {
                  setClusterFilter(val);
                  if (setStandardFilter) setStandardFilter('all');
                  if (setCombinationFilter) setCombinationFilter('all');
                }}
                style={{ width: '100%' }}
                placeholder="Cluster"
              >
                {!isStudio && <Option value="all">All Clusters</Option>}
                {(availableClusters || []).map(c => (
                  <Option key={c.cluster_id} value={String(c.cluster_id)}>
                    {c.cluster_name} {c.cluster_code ? `(${c.cluster_code})` : ''}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {/* 4. Standard Filter (Visible for Candidates / All) */}
          {!isStaffSelected && (
            <Col xs={12} sm={6} md={3}>
              <Select
                value={standardFilter}
                onChange={(val) => {
                  setStandardFilter(val);
                  if (setCombinationFilter) setCombinationFilter('all');
                }}
                style={{ width: '100%' }}
                placeholder="Standard"
              >
                <Option value="all">All Standards</Option>
                {availableStandards.map(st => (
                  <Option key={st.standard_id} value={String(st.standard_id)}>
                    {st.standard_name}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {/* 5. Combination Name Filter (Visible for Candidates / All) */}
          {!isStaffSelected && (
            <Col xs={12} sm={6} md={3}>
              <Select
                value={combinationFilter || 'all'}
                onChange={setCombinationFilter}
                style={{ width: '100%' }}
                placeholder="Combination"
              >
                <Option value="all">All Combinations</Option>
                {(availableCombinations || []).map(comb => (
                  <Option key={comb} value={comb}>
                    {comb}
                  </Option>
                ))}
              </Select>
            </Col>
          )}

          {/* 6. Date Filter */}
          <Col xs={24} sm={12} md={isStaffSelected ? 5 : 3}>
            <DatePicker
              style={{ width: '100%' }}
              value={selectedDate}
              onChange={(d) => setSelectedDate(d)}
              format="DD/MM/YYYY"
              allowClear={false}
            />
          </Col>

          {/* 7. Search Filter */}
          <Col xs={24} sm={12} md={isStaffSelected ? 5 : 3}>
            <Input
              placeholder="Search name, ID, PIN, comb..."
              prefix={<SearchOutlined />}
              value={attSearch}
              onChange={(e) => setAttSearch(e.target.value)}
              allowClear
            />
          </Col>

          {/* Action Buttons */}
          <Col xs={24} sm={24} md={isStaffSelected ? 6 : 24} lg={isStaffSelected ? 6 : 24} style={{ textAlign: isStaffSelected ? 'right' : 'right', marginTop: isStaffSelected ? 0 : 4 }}>
            <Space wrap>
              <Button
                icon={<SettingOutlined />}
                onClick={onOpenShiftSettingsModal}
                title="Configure Shift Timings, Late Cutoff, and Standard Hours"
              >
                Shift Timings
              </Button>
              <Button
                icon={<SyncOutlined spin={recalculating} />}
                onClick={onRecalculate}
                loading={recalculating}
                title="Recalculate attendance for selected date (recreates deleted records from punch logs)"
              >
                Recalculate
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenAttModal()}>
                Add Attendance
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Attendance Data Table */}
      <Table
        columns={columns}
        dataSource={filteredAttendance}
        rowKey="attendance_id"
        loading={attLoading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        bordered
        scroll={{ x: 'max-content' }}
        style={{ background: 'var(--panel-color)' }}
      />
    </div>
  );
}
